import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExternalDataService, CustomerAddress, location } from '../../../services/externalDataService.service';

export interface UpdateCustomerAddress {
  addressId: string;
  address: string;
  pincode: string;
  location: location;
  locationName: string;
  isPrimary: boolean;
  addressType?: string | null;
}

@Component({
  selector: 'app-edit-customer-address',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './edit-customer-address.component.html',
  styleUrl: './edit-customer-address.component.css'
})
export class EditCustomerAddressComponent implements OnInit, OnChanges {
  @Input() address: CustomerAddress | null = null;
  @Input() showModal = false;
  @Output() addressUpdated = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  addressForm: FormGroup;
  isLoading = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private externalDataService: ExternalDataService
  ) {
    this.addressForm = this.fb.group({
      address: ['', [Validators.required, Validators.minLength(10)]],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      locationName: ['', [Validators.required]],
      latitude: [0, [Validators.required]],
      longitude: [0, [Validators.required]],
      isPrimary: [false],
      addressType: ['']
    });
  }

  ngOnInit(): void {
    this.loadAddressData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['address'] && changes['address'].currentValue) {
      this.loadAddressData();
    }
  }

  private loadAddressData(): void {
    if (this.address) {
      this.addressForm.patchValue({
        address: this.address.address,
        pincode: this.address.pincode,
        locationName: this.address.locationName,
        latitude: this.address.location.latitude,
        longitude: this.address.location.longitude,
        isPrimary: this.address.isPrimary,
        addressType: this.address.addressType || ''
      });
    }
  }

  onSubmit(): void {
    if (this.addressForm.valid && this.address) {
      this.isLoading = true;
      this.errorMessage = '';

      const formValue = this.addressForm.value;
      const addressData: UpdateCustomerAddress = {
        addressId: this.address.addressId,
        address: formValue.address,
        pincode: formValue.pincode,
        location: {
          latitude: formValue.latitude,
          longitude: formValue.longitude
        },
        locationName: formValue.locationName,
        isPrimary: formValue.isPrimary,
        addressType: formValue.addressType || null
      };

      this.externalDataService.updateCustomerAddress(addressData)
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.addressUpdated.emit();
            this.resetForm();
          },
          error: (error) => {
            this.isLoading = false;
            this.errorMessage = error.error?.message || 'Failed to update address. Please try again.';
          }
        });
    }
  }

  onCancel(): void {
    this.closeModal.emit();
    this.resetForm();
  }

  private resetForm(): void {
    if (this.address) {
      this.loadAddressData();
    } else {
      this.addressForm.reset({
        address: '',
        pincode: '',
        locationName: '',
        latitude: 0,
        longitude: 0,
        isPrimary: false
      });
    }
    this.errorMessage = '';
  }

  // Method to set location from map selector
  setLocation(lat: number, lng: number): void {
    this.addressForm.patchValue({
      latitude: lat,
      longitude: lng
    });
  }
}

